// src/components/assistant/activeThread.ts
//
// 🔒 SINGLE-SOURCE · hilo ACTIVO compartido entre el chat flotante (orbe del shell
// Orion / FAB del Layout) y el chat a pantalla completa (/chat y Asistente del
// shell). «Ampliar» y «Minimizar» llevan la MISMA conversación de un lado al otro.
//
//   localStorage `orion_active_thread` = { org, id, at }  → sobrevive recargas
//   evento window `orion-active-thread` (detail: { id })   → superficies montadas
//   sessionStorage `orion_dock_open`                        → «Minimizar» pide que
//      el flotante se abra al llegar a la pantalla de destino (aún no montado).
//
// Multi-tenant: el hilo se guarda CON su org y solo se devuelve si coincide con la
// org de la sesión (nunca se reabre el hilo de otra org al cambiar de cuenta).

import { useEffect, useRef, useState } from 'react';

const KEY = 'orion_active_thread';
const OPEN_KEY = 'orion_dock_open';
export const ACTIVE_THREAD_EVENT = 'orion-active-thread';
export const DOCK_OPEN_EVENT = 'orion-dock-open';

function currentOrg(): string {
  try {
    return localStorage.getItem('org_id') || '';
  } catch {
    return '';
  }
}

export function getActiveThread(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { org?: string; id?: string };
    const org = currentOrg();
    return v?.id && org && v.org === org ? v.id : null;
  } catch {
    return null;
  }
}

export function setActiveThread(id: string | null): void {
  const org = currentOrg();
  if (!org) return;
  try {
    if (id) localStorage.setItem(KEY, JSON.stringify({ org, id, at: Date.now() }));
    else localStorage.removeItem(KEY);
  } catch {
    /* almacenamiento bloqueado: el flotante cae al hilo del usuario */
  }
  window.dispatchEvent(new CustomEvent(ACTIVE_THREAD_EVENT, { detail: { id } }));
}

/** «Minimizar»: deja el hilo activo y pide que el chat flotante se abra con él. */
export function requestDockOpen(id: string | null): void {
  if (id) setActiveThread(id);
  try {
    sessionStorage.setItem(OPEN_KEY, '1');
  } catch {
    /* sin sessionStorage: el evento de abajo basta si el flotante ya está montado */
  }
  window.dispatchEvent(new CustomEvent(DOCK_OPEN_EVENT, { detail: { id } }));
}

/** El flotante, al montarse, consume la petición pendiente de abrirse. */
export function consumeDockOpen(): boolean {
  try {
    if (sessionStorage.getItem(OPEN_KEY) !== '1') return false;
    sessionStorage.removeItem(OPEN_KEY);
    return true;
  } catch {
    return false;
  }
}

/** Estado de navegación que deja «Ampliar» para que «Minimizar» regrese ahí. */
export interface DockReturnState {
  fromDock?: string;
}

/**
 * Estado del chat flotante: abierto/cerrado + hilo a mostrar. Lo usan el orbe del
 * shell Orion (AgentDock) y el FAB del Layout clásico, para que ambos sigan la
 * misma conversación que la pantalla completa.
 *  - el hilo solo cambia con el panel CERRADO (abierto, lo publica su ChatPane);
 *  - al abrir retoma el hilo activo (p. ej. elegido en la pestaña Chats);
 *  - «Minimizar» (requestDockOpen) lo abre, esté montado o recién montándose.
 */
export function useSharedDock() {
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState<string | null>(() => getActiveThread());
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (consumeDockOpen()) setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const a = getActiveThread();
    if (a) setThread((cur) => (cur === a ? cur : a));
  }, [open]);

  useEffect(() => {
    const onActive = (e: Event) => {
      const id = (e as CustomEvent<{ id: string | null }>).detail?.id ?? null;
      if (!openRef.current && id) setThread(id);
    };
    const onOpen = (e: Event) => {
      consumeDockOpen();
      const id = (e as CustomEvent<{ id: string | null }>).detail?.id ?? getActiveThread();
      if (id) setThread(id);
      setOpen(true);
    };
    window.addEventListener(ACTIVE_THREAD_EVENT, onActive);
    window.addEventListener(DOCK_OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener(ACTIVE_THREAD_EVENT, onActive);
      window.removeEventListener(DOCK_OPEN_EVENT, onOpen);
    };
  }, []);

  return { open, setOpen, thread };
}
